import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  FlaskConical,
  Users,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  Plus,
  Edit,
  Trash2,
  Search,
  IndianRupee,
  Eye,
  X,
  Check,
  AlertCircle,
  Image,
  Gift,
  FileText,
  Download,
  Microscope,
  Presentation,
  Shield,
  Phone,
  MapPin,
  Mail,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import ImageUpload from '../components/ImageUpload';

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data states
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [loyaltyProgram, setLoyaltyProgram] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [testDetails, setTestDetails] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroDirectors, setHeroDirectors] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [differentiators, setDifferentiators] = useState([]);
  const [aboutSettings, setAboutSettings] = useState({ hero_image: '', hero_image_alt: '' });
  const [downloadsPageSettings, setDownloadsPageSettings] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'heroSlides', label: 'Hero Section', icon: Presentation },
    { id: 'aboutPage', label: 'About Page', icon: Users },
    { id: 'packages', label: 'Packages', icon: Package },
    { id: 'services', label: 'Services', icon: FlaskConical },
    { id: 'testDetails', label: 'Test Details', icon: Microscope },
    { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'loyalty', label: 'Loyalty Program', icon: Gift },
    { id: 'downloads', label: 'Downloads Page', icon: Download },
    { id: 'contactUs', label: 'Contact Us', icon: Phone },
    { id: 'blogs', label: 'Blogs', icon: FileText },
    { id: 'inquiries', label: 'Inquiries', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'auditLogs', label: 'Audit Logs', icon: Shield },
  ];

  // loadData fetches data based on activeTab - adminAPI is a stable module import
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      switch (activeTab) {
        case 'dashboard':
          const statsRes = await adminAPI.getDashboardStats();
          setStats(statsRes.data);
          const inqRes = await adminAPI.getInquiries();
          setInquiries(inqRes.data.slice(0, 5));
          break;
        case 'packages':
          const pkgRes = await adminAPI.getPackages();
          console.log('Packages received from API:', pkgRes.data.length, pkgRes.data);
          // Filter to only show Advanced and Smart Wellness packages
          const validPackages = pkgRes.data.filter(p => 
            p.category === 'Advanced Wellness Packages' || p.category === 'Smart Wellness Packages'
          );
          console.log('Valid packages after filter:', validPackages.length);
          setPackages(validPackages);
          break;
        case 'services':
          const svcRes = await adminAPI.getServices();
          setServices(svcRes.data);
          break;
        case 'testimonials':
          const testRes = await adminAPI.getTestimonials();
          setTestimonials(testRes.data);
          break;
        case 'inquiries':
          const allInqRes = await adminAPI.getInquiries();
          setInquiries(allInqRes.data);
          break;
        case 'settings':
          const settingsRes = await adminAPI.getSettings();
          setSettings(settingsRes.data);
          break;
        case 'gallery':
          const galleryRes = await adminAPI.getGallery();
          setGallery(galleryRes.data);
          break;
        case 'loyalty':
          const loyaltyRes = await adminAPI.getLoyaltyProgram();
          setLoyaltyProgram(loyaltyRes.data);
          break;
        case 'blogs':
          const blogsRes = await adminAPI.getBlogs();
          setBlogs(blogsRes.data);
          break;
        case 'testDetails':
          const testDetailsRes = await adminAPI.getTestDetails();
          setTestDetails(testDetailsRes.data);
          break;
        case 'heroSlides':
          const heroRes = await adminAPI.getHeroSlides();
          setHeroSlides(heroRes.data);
          const heroDirectorsRes = await adminAPI.getHeroDirectors();
          setHeroDirectors(heroDirectorsRes.data || []);
          break;
        case 'aboutPage':
          const directorsRes = await adminAPI.getDirectors();
          setDirectors(directorsRes.data);
          const consultantsRes = await adminAPI.getConsultants();
          setConsultants(consultantsRes.data);
          const differentiatorRes = await adminAPI.getDifferentiators();
          setDifferentiators(differentiatorRes.data);
          const aboutSettingsRes = await adminAPI.getAboutSettings();
          setAboutSettings(aboutSettingsRes.data);
          break;
        case 'downloads':
          const downloadsSettingsRes = await adminAPI.getDownloadsPageSettings();
          setDownloadsPageSettings(downloadsSettingsRes.data);
          break;
        case 'contactUs':
          const contactRes = await adminAPI.getContactInfo();
          setContactInfo(contactRes.data);
          break;
        case 'auditLogs':
          const logsRes = await adminAPI.getAuditLogs(50);
          setAuditLogs(logsRes.data);
          break;
        default:
          break;
      }
    } catch (err) {
      setError('Failed to load data: ' + (err.message || 'Unknown error'));
      console.error('LoadData error:', err);
    }
    setLoading(false);
  }, [activeTab]);

  // Load data on initial mount and when tab changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Memoized filtered and sorted data to avoid expensive computations on every render
  const advancedWellnessPackages = useMemo(() => 
    packages
      .filter(p => p.category === 'Advanced Wellness Packages' && p.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999)),
    [packages, searchTerm]
  );

  const smartWellnessPackages = useMemo(() => 
    packages
      .filter(p => p.category === 'Smart Wellness Packages' && p.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [packages, searchTerm]
  );

  const radiologyConsultants = useMemo(() => 
    consultants.filter(c => c.department === 'radiology'),
    [consultants]
  );

  const laboratoryConsultants = useMemo(() => 
    consultants.filter(c => c.department === 'laboratory'),
    [consultants]
  );

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    if (type === 'editTest' && item?.test) {
      // Pre-fill form data for editing a test
      const test = item.test;
      setFormData({
        testName: typeof test === 'string' ? test : test.name,
        testDescription: typeof test === 'object' ? test.description : ''
      });
    } else if (item) {
      setFormData({ ...item });
    } else {
      setFormData({});
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      switch (modalType) {
        case 'package':
          // Ensure all required fields and arrays exist
          const packageData = {
            name: formData.name,
            price: parseInt(formData.price) || 0,
            original_price: parseInt(formData.original_price) || 0,
            tests: formData.tests || [],
            preparations: formData.preparations || [],
            description: formData.description || '',
            category: formData.category || 'Smart Wellness Packages',
            report_time: formData.report_time || 'Same Day',
            is_active: formData.is_active !== false,
            featured: formData.featured || false
          };
          console.log('Submitting package data:', packageData);
          if (editingItem) {
            const updateRes = await adminAPI.updatePackage(editingItem.id, packageData);
            console.log('Update response:', updateRes);
            setSuccess('Package updated successfully');
          } else {
            const createRes = await adminAPI.createPackage(packageData);
            console.log('Create response:', createRes);
            setSuccess('Package created successfully');
          }
          break;
        case 'service':
          if (editingItem) {
            await adminAPI.updateService(editingItem.id, formData);
            setSuccess('Service updated successfully');
          } else {
            await adminAPI.createService(formData);
            setSuccess('Service created successfully');
          }
          break;
        case 'testimonial':
          if (editingItem) {
            await adminAPI.updateTestimonial(editingItem.id, formData);
            setSuccess('Testimonial updated successfully');
          } else {
            await adminAPI.createTestimonial(formData);
            setSuccess('Testimonial created successfully');
          }
          break;
        case 'inquiry':
          await adminAPI.updateInquiry(editingItem.id, formData);
          setSuccess('Inquiry updated successfully');
          break;
        case 'gallery':
          if (editingItem) {
            await adminAPI.updateGalleryImage(editingItem.id, formData);
            setSuccess('Gallery image updated successfully');
          } else {
            await adminAPI.createGalleryImage(formData);
            setSuccess('Gallery image added successfully');
          }
          break;
        case 'blog':
          if (editingItem) {
            await adminAPI.updateBlog(editingItem.id, formData);
            setSuccess('Blog post updated successfully');
          } else {
            await adminAPI.createBlog(formData);
            setSuccess('Blog post created successfully');
          }
          break;
        case 'testDetail':
          if (editingItem) {
            await adminAPI.updateTestDetail(editingItem.id, formData);
            setSuccess('Test detail updated successfully');
          } else {
            await adminAPI.createTestDetail(formData);
            setSuccess('Test detail created successfully');
          }
          break;
        case 'heroSlide':
          if (editingItem) {
            await adminAPI.updateHeroSlide(editingItem.id, formData);
            setSuccess('Hero slide updated successfully');
          } else {
            await adminAPI.createHeroSlide(formData);
            setSuccess('Hero slide created successfully');
          }
          break;
        case 'heroDirector':
          if (editingItem) {
            await adminAPI.updateHeroDirector(editingItem.id, formData);
            setSuccess('Hero director updated successfully');
          }
          break;
        case 'director':
          if (editingItem) {
            await adminAPI.updateDirector(editingItem.id, formData);
            setSuccess('Director updated successfully');
          } else {
            await adminAPI.createDirector(formData);
            setSuccess('Director added successfully');
          }
          break;
        case 'consultant':
          // Validate required fields - use default department if not set
          const consultantDepartment = formData.department || 'laboratory';
          if (!formData.name || !formData.role || !formData.qualifications) {
            setError('Please fill in all required fields');
            return;
          }
          // Image is optional - can be added later
          const consultantData = {
            name: formData.name,
            role: formData.role,
            qualifications: formData.qualifications,
            department: consultantDepartment,
            image: formData.image || '',
            description: formData.description || ''
          };
          if (editingItem) {
            await adminAPI.updateConsultant(editingItem.id, consultantData);
            setSuccess('Consultant updated successfully');
          } else {
            await adminAPI.createConsultant(consultantData);
            setSuccess('Consultant added successfully');
          }
          break;
        case 'differentiator':
          if (editingItem) {
            await adminAPI.updateDifferentiator(editingItem.id, formData);
            setSuccess('Feature updated successfully');
          } else {
            await adminAPI.createDifferentiator(formData);
            setSuccess('Feature added successfully');
          }
          break;
        case 'subservice':
          // Handle sub-service add/edit
          const serviceToUpdate = services.find(s => s.id === editingItem.serviceId);
          if (serviceToUpdate) {
            const updatedSubServices = [...(serviceToUpdate.sub_services || [])];
            const newSubService = {
              id: editingItem.subService?.id || `sub_${Date.now()}`,
              name: formData.name,
              slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-'),
              description: formData.description || '',
              tests: editingItem.subService?.tests || []
            };
            
            if (editingItem.subIndex !== undefined) {
              updatedSubServices[editingItem.subIndex] = newSubService;
            } else {
              updatedSubServices.push(newSubService);
            }
            
            await adminAPI.updateService(editingItem.serviceId, { sub_services: updatedSubServices });
            setSuccess(editingItem.subIndex !== undefined ? 'Study updated successfully' : 'Study added successfully');
          }
          break;
        case 'test':
          // Handle test add
          const serviceForTest = services.find(s => s.id === editingItem.serviceId);
          if (serviceForTest) {
            const updatedSubs = [...(serviceForTest.sub_services || [])];
            if (updatedSubs[editingItem.subIndex]) {
              const newTest = { name: formData.testName, description: formData.testDescription || '' };
              updatedSubs[editingItem.subIndex].tests = [...(updatedSubs[editingItem.subIndex].tests || []), newTest];
              await adminAPI.updateService(editingItem.serviceId, { sub_services: updatedSubs });
              setSuccess('Test added successfully');
            }
          }
          break;
        case 'editTest':
          // Handle test edit
          const serviceToEdit = services.find(s => s.id === editingItem.serviceId);
          if (serviceToEdit) {
            const updatedSubsForEdit = [...(serviceToEdit.sub_services || [])];
            if (updatedSubsForEdit[editingItem.subIndex] && updatedSubsForEdit[editingItem.subIndex].tests) {
              updatedSubsForEdit[editingItem.subIndex].tests[editingItem.testIndex] = { 
                name: formData.testName, 
                description: formData.testDescription || '' 
              };
              await adminAPI.updateService(editingItem.serviceId, { sub_services: updatedSubsForEdit });
              setSuccess('Test updated successfully');
            }
          }
          break;
        case 'download':
          if (editingItem) {
            await adminAPI.updateDownload(editingItem.id, formData);
            setSuccess('Download updated successfully');
          } else {
            await adminAPI.createDownload(formData);
            setSuccess('Download added successfully');
          }
          break;
        default:
          break;
      }
      closeModal();
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      switch (type) {
        case 'package':
          await adminAPI.deletePackage(id);
          break;
        case 'service':
          await adminAPI.deleteService(id);
          break;
        case 'testimonial':
          await adminAPI.deleteTestimonial(id);
          break;
        case 'inquiry':
          await adminAPI.deleteInquiry(id);
          break;
        case 'gallery':
          await adminAPI.deleteGalleryImage(id);
          break;
        case 'blog':
          await adminAPI.deleteBlog(id);
          break;
        case 'testDetail':
          await adminAPI.deleteTestDetail(id);
          break;
        case 'heroSlide':
          await adminAPI.deleteHeroSlide(id);
          break;
        case 'director':
          await adminAPI.deleteDirector(id);
          break;
        case 'consultant':
          await adminAPI.deleteConsultant(id);
          break;
        case 'differentiator':
          await adminAPI.deleteDifferentiator(id);
          break;
        case 'download':
          await adminAPI.deleteDownload(id);
          break;
        default:
          break;
      }
      setSuccess('Item deleted successfully');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete item');
    }
  };

  const handleDeleteTest = async (serviceId, subIndex, testIndex) => {
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    
    try {
      const serviceToUpdate = services.find(s => s.id === serviceId);
      if (serviceToUpdate && serviceToUpdate.sub_services) {
        const updatedSubServices = [...serviceToUpdate.sub_services];
        if (updatedSubServices[subIndex] && updatedSubServices[subIndex].tests) {
          updatedSubServices[subIndex].tests = updatedSubServices[subIndex].tests.filter((_, i) => i !== testIndex);
          await adminAPI.updateService(serviceId, { sub_services: updatedSubServices });
          setSuccess('Test deleted successfully');
          loadData();
          setTimeout(() => setSuccess(''), 3000);
        }
      }
    } catch (err) {
      setError('Failed to delete test');
    }
  };

  const handleSettingsSave = async () => {
    try {
      await adminAPI.updateSettings(settings);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save settings');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-[#007897] text-white transition-all duration-300 z-50 ${
        sidebarOpen ? 'w-64' : 'w-20'
      }`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="font-bold">ND</span>
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="font-bold">Nanda Diagnostics</h1>
                <p className="text-xs text-blue-200">Admin Panel</p>
              </div>
            )}
          </div>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                activeTab === item.id
                  ? 'bg-white/20 text-white'
                  : 'text-blue-200 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon size={18} />
              {sidebarOpen && <span className="text-sm">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 space-y-1 bg-[#007897]">
          <Link to="/">
            <button className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-blue-200 hover:bg-white/10 hover:text-white transition-colors">
              <Eye size={18} />
              {sidebarOpen && <span className="text-sm">View Website</span>}
            </button>
          </Link>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-blue-200 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut size={18} />
            {sidebarOpen && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Header */}
        <header className="bg-white shadow-sm px-6 py-4 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Menu size={20} />
            </button>
            <h2 className="text-xl font-semibold text-gray-800 capitalize">{activeTab}</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search..."
                className="pl-10 w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#007897] rounded-full flex items-center justify-center text-white font-medium">
                {user?.full_name?.charAt(0) || 'A'}
              </div>
              {sidebarOpen && <span className="text-sm text-gray-700">{user?.full_name}</span>}
            </div>
          </div>
        </header>

        {/* Alerts */}
        <div className="p-6 pb-0">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
              <AlertCircle size={18} />
              {error}
              <button onClick={() => setError('')} className="ml-auto"><X size={18} /></button>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
              <Check size={18} />
              {success}
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#007897] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {activeTab === 'dashboard' && stats && (
                <div className="space-y-6">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Total Packages</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.packages}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                          <Package size={24} className="text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Services</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.services}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                          <FlaskConical size={24} className="text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Testimonials</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.testimonials}</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                          <MessageSquare size={24} className="text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Inquiries</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.inquiries?.total || 0}</p>
                        </div>
                        <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                          <Users size={24} className="text-white" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Inquiries */}
                  <div className="bg-white rounded-xl shadow-sm">
                    <div className="p-6 border-b flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Recent Inquiries</h3>
                      <Button variant="outline" size="sm" onClick={() => setActiveTab('inquiries')}>View All</Button>
                    </div>
                    {inquiries.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {inquiries.map((inquiry) => (
                              <tr key={inquiry.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{inquiry.name}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{inquiry.phone}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{inquiry.email}</td>
                                <td className="px-6 py-4">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(inquiry.status)}`}>
                                    {inquiry.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="p-6 text-gray-500 text-center">No inquiries yet</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'packages' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Packages & Tests</h3>
                    <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('package')}>
                      <Plus size={18} className="mr-2" /> Add Package
                    </Button>
                  </div>
                  
                  {/* Active Packages Summary */}
                  <div className="bg-gradient-to-r from-[#007897] to-[#006680] rounded-xl p-4 text-white">
                    <h4 className="font-medium mb-2">Active Packages on Website</h4>
                    <div className="flex items-center gap-4">
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                        {packages.filter(p => p.is_active !== false).length} Active
                      </span>
                      <span className="bg-white/10 px-3 py-1 rounded-full text-sm">
                        {packages.filter(p => p.is_active === false).length} Inactive
                      </span>
                    </div>
                  </div>
                  
                  {/* Advanced Wellness Packages */}
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-gray-700 border-b pb-2">Advanced Wellness Packages</h4>
                    {advancedWellnessPackages.map((pkg) => (
                      <div key={pkg.id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${pkg.is_active === false ? 'opacity-60' : ''}`}>
                        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">{pkg.name}</h4>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-[#007897] font-medium flex items-center"><IndianRupee size={14} />{pkg.price?.toLocaleString()}</span>
                                <span className="text-xs text-gray-400 line-through flex items-center"><IndianRupee size={12} />{(pkg.original_price || pkg.originalPrice)?.toLocaleString()}</span>
                                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">{pkg.category}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={async () => {
                                try {
                                  await adminAPI.updatePackage(pkg.id, { is_active: !pkg.is_active });
                                  setPackages(packages.map(p => p.id === pkg.id ? { ...p, is_active: !p.is_active } : p));
                                } catch (error) {
                                  console.error('Error toggling package status:', error);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                pkg.is_active !== false
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {pkg.is_active !== false ? 'Active' : 'Inactive'}
                            </button>
                            <button className="p-2 hover:bg-white rounded-lg border" onClick={() => openModal('package', pkg)}>
                              <Edit size={16} className="text-blue-500" />
                            </button>
                            <button className="p-2 hover:bg-white rounded-lg border" onClick={() => handleDelete('package', pkg.id)}>
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-xs font-medium text-gray-500 mb-2">Tests Included ({pkg.tests?.length || 0})</p>
                          <div className="flex flex-wrap gap-2">
                            {pkg.tests && pkg.tests.map((test, index) => (
                              <span key={`item-${index}`} className="bg-gray-100 text-gray-700 text-xs px-3 py-1.5 rounded-full">{test}</span>
                            ))}
                            {(!pkg.tests || pkg.tests.length === 0) && <span className="text-xs text-gray-400 italic">No tests specified</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Smart Wellness Packages - Alphabetically Sorted */}
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-gray-700 border-b pb-2">Smart Wellness Packages (A-Z)</h4>
                    {smartWellnessPackages.map((pkg) => (
                      <div key={pkg.id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${pkg.is_active === false ? 'opacity-60' : ''}`}>
                        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">{pkg.name}</h4>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-[#007897] font-medium flex items-center"><IndianRupee size={14} />{pkg.price?.toLocaleString()}</span>
                                <span className="text-xs text-gray-400 line-through flex items-center"><IndianRupee size={12} />{(pkg.original_price || pkg.originalPrice)?.toLocaleString()}</span>
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{pkg.category}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={async () => {
                                try {
                                  await adminAPI.updatePackage(pkg.id, { is_active: !pkg.is_active });
                                  setPackages(packages.map(p => p.id === pkg.id ? { ...p, is_active: !p.is_active } : p));
                                } catch (error) {
                                  console.error('Error toggling package status:', error);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                pkg.is_active !== false
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {pkg.is_active !== false ? 'Active' : 'Inactive'}
                            </button>
                            <button className="p-2 hover:bg-white rounded-lg border" onClick={() => openModal('package', pkg)}>
                              <Edit size={16} className="text-blue-500" />
                            </button>
                            <button className="p-2 hover:bg-white rounded-lg border" onClick={() => handleDelete('package', pkg.id)}>
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-xs font-medium text-gray-500 mb-2">Tests Included ({pkg.tests?.length || 0})</p>
                          <div className="flex flex-wrap gap-2">
                            {pkg.tests && pkg.tests.map((test, index) => (
                              <span key={`item-${index}`} className="bg-gray-100 text-gray-700 text-xs px-3 py-1.5 rounded-full">{test}</span>
                            ))}
                            {(!pkg.tests || pkg.tests.length === 0) && <span className="text-xs text-gray-400 italic">No tests specified</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                    {packages.length === 0 && (
                      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <Package size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No packages yet. Add your first package!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'services' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Services & Tests</h3>
                    <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('service')}>
                      <Plus size={18} className="mr-2" /> Add Service
                    </Button>
                  </div>
                  
                  {services.length > 0 ? services.map((service) => (
                    <div key={service.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                      {/* Service Header */}
                      <div className="p-6 border-b bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">{service.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs font-medium bg-[#007897] text-white px-2 py-1 rounded">{service.category}</span>
                              <span className="text-xs text-gray-500">{service.sub_services?.length || 0} studies</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button className="p-2 hover:bg-white rounded-lg border" onClick={() => openModal('service', service)}>
                              <Edit size={16} className="text-blue-500" />
                            </button>
                            <button className="p-2 hover:bg-white rounded-lg border" onClick={() => handleDelete('service', service.id)}>
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                            <button className="p-2 hover:bg-white rounded-lg border bg-green-50" onClick={() => openModal('subservice', { serviceId: service.id, serviceName: service.name })}>
                              <Plus size={16} className="text-green-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Sub-services (Studies) */}
                      <div className="p-6">
                        {service.sub_services && service.sub_services.length > 0 ? (
                          <div className="space-y-4">
                            {service.sub_services.map((subService, subIndex) => (
                              <div key={subService.id || subIndex} className="border rounded-lg p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <h5 className="font-medium text-gray-900">{subService.name}</h5>
                                    <p className="text-xs text-gray-500 mt-1">{subService.description}</p>
                                  </div>
                                  <div className="flex gap-1">
                                    <button 
                                      className="p-1.5 hover:bg-gray-100 rounded text-blue-500"
                                      onClick={() => openModal('subservice', { serviceId: service.id, serviceName: service.name, subService, subIndex })}
                                    >
                                      <Edit size={14} />
                                    </button>
                                    <button 
                                      className="p-1.5 hover:bg-gray-100 rounded text-green-600"
                                      onClick={() => openModal('test', { serviceId: service.id, subIndex, subServiceName: subService.name })}
                                    >
                                      <Plus size={14} />
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Tests */}
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-gray-500 mb-2">Tests ({subService.tests?.length || 0})</p>
                                  <div className="flex flex-wrap gap-2">
                                    {subService.tests && subService.tests.map((test, testIndex) => (
                                      <div 
                                        key={test.id || `test-${service.id}-${subIndex}-${testIndex}`} 
                                        className="group relative bg-gray-100 text-gray-700 text-xs px-3 py-1.5 rounded-full flex items-center gap-1"
                                      >
                                        <span>{test.name || test}</span>
                                        <button 
                                          className="ml-1 opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700"
                                          onClick={() => openModal('editTest', { serviceId: service.id, subIndex, testIndex, test, subServiceName: subService.name })}
                                          title="Edit test"
                                        >
                                          <Edit size={12} />
                                        </button>
                                        <button 
                                          className="ml-1 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                                          onClick={() => handleDeleteTest(service.id, subIndex, testIndex)}
                                          title="Delete test"
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    ))}
                                    {(!subService.tests || subService.tests.length === 0) && (
                                      <span className="text-xs text-gray-400 italic">No tests added yet</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-4">No studies added. Click + to add a study.</p>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                      <FlaskConical size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No services yet. Add your first service!</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'testimonials' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Testimonials</h3>
                    <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('testimonial')}>
                      <Plus size={18} className="mr-2" /> Add Testimonial
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    {testimonials.length > 0 ? testimonials.map((testimonial) => (
                      <div key={testimonial.id} className="bg-white rounded-xl shadow-sm p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                            <p className="text-sm text-gray-500">{testimonial.source}</p>
                          </div>
                          <div className="flex gap-2">
                            <button className="p-1 hover:bg-gray-100 rounded" onClick={() => openModal('testimonial', testimonial)}>
                              <Edit size={16} className="text-blue-500" />
                            </button>
                            <button className="p-1 hover:bg-gray-100 rounded" onClick={() => handleDelete('testimonial', testimonial.id)}>
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-3">&ldquo;{testimonial.review}&rdquo;</p>
                        <div className="flex items-center gap-1 mt-4">
                          {[...Array(5)].map((_, i) => (
                            <span key={`star-${testimonial.id}-${i}`} className={`text-lg ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}>
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                    )) : (
                      <p className="col-span-2 p-6 text-gray-500 text-center">No testimonials yet. Add your first testimonial!</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'inquiries' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">All Inquiries</h3>
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    {inquiries.length > 0 ? (
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {inquiries.map((inquiry) => (
                            <tr key={inquiry.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{inquiry.name}</td>
                              <td className="px-6 py-4 text-sm text-gray-600">{inquiry.phone}</td>
                              <td className="px-6 py-4 text-sm text-gray-600">{inquiry.email}</td>
                              <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{inquiry.message}</td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(inquiry.status)}`}>
                                  {inquiry.status}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex gap-2">
                                  <button className="p-1 hover:bg-gray-100 rounded" onClick={() => openModal('inquiry', inquiry)}>
                                    <Edit size={16} className="text-blue-500" />
                                  </button>
                                  <button className="p-1 hover:bg-gray-100 rounded" onClick={() => handleDelete('inquiry', inquiry.id)}>
                                    <Trash2 size={16} className="text-red-500" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="p-6 text-gray-500 text-center">No inquiries yet</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'settings' && settings && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Settings</h3>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Company Information</h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label>Company Name</Label>
                        <Input value={settings.name || ''} onChange={(e) => setSettings({...settings, name: e.target.value})} />
                      </div>
                      <div>
                        <Label>Tagline</Label>
                        <Input value={settings.tagline || ''} onChange={(e) => setSettings({...settings, tagline: e.target.value})} />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Address</Label>
                        <Input value={settings.address || ''} onChange={(e) => setSettings({...settings, address: e.target.value})} />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input value={settings.email || ''} onChange={(e) => setSettings({...settings, email: e.target.value})} />
                      </div>
                      <div>
                        <Label>WhatsApp</Label>
                        <Input value={settings.whatsapp || ''} onChange={(e) => setSettings({...settings, whatsapp: e.target.value})} />
                      </div>
                    </div>
                    <Button className="mt-6 bg-[#007897] hover:bg-[#006680]" onClick={handleSettingsSave}>Save Changes</Button>
                  </div>
                </div>
              )}

              {activeTab === 'gallery' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Gallery</h3>
                    <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('gallery')}>
                      <Plus size={18} className="mr-2" /> Add Image
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {gallery.length > 0 ? gallery.map((img) => (
                      <div key={img.id} className="bg-white rounded-xl shadow-sm overflow-hidden group">
                        <div className="relative h-48">
                          <img 
                            src={img.image} 
                            alt={img.title} 
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="%23f3f4f6" width="400" height="300"/><text fill="%239ca3af" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle">No Image</text></svg>'; }}
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button className="p-2 bg-white rounded-lg hover:bg-gray-100" onClick={() => openModal('gallery', img)}>
                              <Edit size={18} className="text-blue-500" />
                            </button>
                            <button className="p-2 bg-white rounded-lg hover:bg-gray-100" onClick={() => handleDelete('gallery', img.id)}>
                              <Trash2 size={18} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                        <div className="p-4">
                          <h4 className="font-medium text-gray-900">{img.title}</h4>
                          <span className="text-xs text-[#007897] bg-blue-50 px-2 py-1 rounded mt-2 inline-block">{img.category}</span>
                        </div>
                      </div>
                    )) : (
                      <p className="col-span-3 p-6 text-gray-500 text-center">No gallery images yet. Add your first image!</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'loyalty' && loyaltyProgram && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Loyalty Program</h3>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label>Title</Label>
                        <Input 
                          value={loyaltyProgram.title || ''} 
                          onChange={(e) => setLoyaltyProgram({...loyaltyProgram, title: e.target.value})} 
                        />
                      </div>
                      <div>
                        <Label>Price (₹)</Label>
                        <Input 
                          type="number"
                          value={loyaltyProgram.price || ''} 
                          onChange={(e) => setLoyaltyProgram({...loyaltyProgram, price: parseInt(e.target.value)})} 
                        />
                      </div>
                      <div>
                        <Label>Validity</Label>
                        <Input 
                          value={loyaltyProgram.validity || ''} 
                          onChange={(e) => setLoyaltyProgram({...loyaltyProgram, validity: e.target.value})} 
                        />
                      </div>
                      <div>
                        <Label>Family Members</Label>
                        <Input 
                          type="number"
                          value={loyaltyProgram.family_members || ''} 
                          onChange={(e) => setLoyaltyProgram({...loyaltyProgram, family_members: parseInt(e.target.value)})} 
                        />
                      </div>
                      <div>
                        <Label>Free Checkup Value (₹)</Label>
                        <Input 
                          type="number"
                          value={loyaltyProgram.free_checkup_value || ''} 
                          onChange={(e) => setLoyaltyProgram({...loyaltyProgram, free_checkup_value: parseInt(e.target.value)})} 
                        />
                      </div>
                      <div>
                        <ImageUpload
                          label="Card Image"
                          value={loyaltyProgram.image || ''}
                          onChange={(url) => setLoyaltyProgram({...loyaltyProgram, image: url})}
                          previewClassName="w-48 h-32 object-cover rounded-lg"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Introduction Text</Label>
                      <Textarea 
                        rows={3}
                        value={loyaltyProgram.intro_text || ''} 
                        onChange={(e) => setLoyaltyProgram({...loyaltyProgram, intro_text: e.target.value})} 
                      />
                    </div>
                    
                    <div>
                      <Label>Description</Label>
                      <Textarea 
                        rows={4}
                        value={loyaltyProgram.description || ''} 
                        onChange={(e) => setLoyaltyProgram({...loyaltyProgram, description: e.target.value})} 
                      />
                    </div>

                    <div>
                      <Label>Free Checkup Tests (comma separated)</Label>
                      <Textarea 
                        rows={2}
                        value={(loyaltyProgram.free_checkup_tests || []).join(', ')} 
                        onChange={(e) => setLoyaltyProgram({...loyaltyProgram, free_checkup_tests: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} 
                      />
                    </div>

                    <div>
                      <Label>Additional Info (one per line)</Label>
                      <Textarea 
                        rows={4}
                        value={(loyaltyProgram.additional_info || []).join('\n')} 
                        onChange={(e) => setLoyaltyProgram({...loyaltyProgram, additional_info: e.target.value.split('\n').filter(Boolean)})} 
                      />
                    </div>

                    <Button 
                      className="bg-[#007897] hover:bg-[#006680]" 
                      onClick={async () => {
                        try {
                          await adminAPI.updateLoyaltyProgram(loyaltyProgram);
                          setSuccess('Loyalty program updated successfully');
                          setTimeout(() => setSuccess(''), 3000);
                        } catch (err) {
                          setError('Failed to update loyalty program');
                        }
                      }}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}

              {/* Downloads Page Settings Section */}
              {activeTab === 'downloads' && downloadsPageSettings && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Downloads Page Content</h3>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                    {/* Hero Section */}
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2 border-b pb-2">
                      <Download size={18} className="text-[#007897]" /> Hero Section
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Hero Title</Label>
                        <Input 
                          value={downloadsPageSettings.hero_title || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, hero_title: e.target.value})}
                          placeholder="Download Reports"
                        />
                      </div>
                      <div>
                        <Label>Hero Subtitle</Label>
                        <Input 
                          value={downloadsPageSettings.hero_subtitle || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, hero_subtitle: e.target.value})}
                          placeholder="Access and download your test reports online"
                        />
                      </div>
                    </div>

                    {/* Form Section */}
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2 border-b pb-2 pt-4">
                      <FileText size={18} className="text-[#007897]" /> Report Download Form
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Form Title</Label>
                        <Input 
                          value={downloadsPageSettings.form_title || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, form_title: e.target.value})}
                          placeholder="Download Report"
                        />
                      </div>
                      <div>
                        <Label>Form Subtitle</Label>
                        <Input 
                          value={downloadsPageSettings.form_subtitle || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, form_subtitle: e.target.value})}
                          placeholder="Access your test results online"
                        />
                      </div>
                      <div>
                        <Label>Lab ID Label</Label>
                        <Input 
                          value={downloadsPageSettings.lab_id_label || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, lab_id_label: e.target.value})}
                          placeholder="Registered Lab ID"
                        />
                      </div>
                      <div>
                        <Label>Lab ID Placeholder</Label>
                        <Input 
                          value={downloadsPageSettings.lab_id_placeholder || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, lab_id_placeholder: e.target.value})}
                          placeholder="Enter your Lab ID"
                        />
                      </div>
                      <div>
                        <Label>Password Label</Label>
                        <Input 
                          value={downloadsPageSettings.password_label || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, password_label: e.target.value})}
                          placeholder="Password"
                        />
                      </div>
                      <div>
                        <Label>Password Placeholder</Label>
                        <Input 
                          value={downloadsPageSettings.password_placeholder || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, password_placeholder: e.target.value})}
                          placeholder="Enter your password"
                        />
                      </div>
                      <div>
                        <Label>Search Button Text</Label>
                        <Input 
                          value={downloadsPageSettings.search_button_text || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, search_button_text: e.target.value})}
                          placeholder="Search Report"
                        />
                      </div>
                    </div>

                    {/* Help Section */}
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2 border-b pb-2 pt-4">
                      <MessageSquare size={18} className="text-[#007897]" /> Help Section
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <Label>Help Title</Label>
                        <Input 
                          value={downloadsPageSettings.help_title || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, help_title: e.target.value})}
                          placeholder="Need Help with Reports?"
                        />
                      </div>
                      <div>
                        <Label>Help Text</Label>
                        <Textarea 
                          rows={3}
                          value={downloadsPageSettings.help_text || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, help_text: e.target.value})}
                          placeholder="If you are unable to find your report online..."
                        />
                      </div>
                    </div>

                    {/* Timing & CTA Section */}
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2 border-b pb-2 pt-4">
                      <Clock size={18} className="text-[#007897]" /> Timing & CTA
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <Label>Report Collection Timing Text</Label>
                        <Input 
                          value={downloadsPageSettings.timing_text || ''} 
                          onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, timing_text: e.target.value})}
                          placeholder="Reports can be collected from the centre between 6-8 pm"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>CTA Title</Label>
                          <Input 
                            value={downloadsPageSettings.cta_title || ''} 
                            onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, cta_title: e.target.value})}
                            placeholder="Have Questions?"
                          />
                        </div>
                        <div>
                          <Label>CTA Text</Label>
                          <Input 
                            value={downloadsPageSettings.cta_text || ''} 
                            onChange={(e) => setDownloadsPageSettings({...downloadsPageSettings, cta_text: e.target.value})}
                            placeholder="Our team is available to help you..."
                          />
                        </div>
                      </div>
                    </div>

                    <Button 
                      className="bg-[#007897] hover:bg-[#006680] mt-4" 
                      onClick={async () => {
                        try {
                          await adminAPI.updateDownloadsPageSettings(downloadsPageSettings);
                          setSuccess('Downloads page settings updated successfully');
                          setTimeout(() => setSuccess(''), 3000);
                        } catch (err) {
                          setError('Failed to update downloads page settings');
                        }
                      }}
                    >
                      Save Downloads Page Settings
                    </Button>
                  </div>
                </div>
              )}

              {/* Contact Us Section */}
              {activeTab === 'contactUs' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Contact Information</h3>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Phone size={18} className="text-[#007897]" /> Contact Numbers
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Main Phone Number</Label>
                        <Input 
                          value={contactInfo?.phones?.[0] || ''} 
                          onChange={(e) => {
                            const phones = [e.target.value];
                            setContactInfo({...contactInfo, phones});
                          }}
                          placeholder="011-47500500"
                        />
                      </div>
                      <div>
                        <Label>WhatsApp Number</Label>
                        <Input 
                          value={contactInfo?.whatsapp || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, whatsapp: e.target.value})}
                          placeholder="+919953852212"
                        />
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2 pt-4">
                      <MapPin size={18} className="text-[#007897]" /> Address
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <Label>Full Address</Label>
                        <Textarea 
                          rows={2}
                          value={contactInfo?.address || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, address: e.target.value})}
                          placeholder="C-566, Outer Ring Road, Saraswati Vihar, Pitam Pura, Delhi – 110034"
                        />
                      </div>
                      <div>
                        <Label>Google Maps Link</Label>
                        <Input 
                          value={contactInfo?.map_url || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, map_url: e.target.value})}
                          placeholder="https://maps.app.goo.gl/..."
                        />
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2 pt-4">
                      <Clock size={18} className="text-[#007897]" /> Working Hours
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Weekday Hours</Label>
                        <Input 
                          value={contactInfo?.working_hours || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, working_hours: e.target.value})}
                          placeholder="Mon-Sat: 8:00 AM - 8:00 PM"
                        />
                      </div>
                      <div>
                        <Label>Sunday Hours</Label>
                        <Input 
                          value={contactInfo?.sunday_hours || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, sunday_hours: e.target.value})}
                          placeholder="Sun: 9:00 AM - 2:00 PM"
                        />
                      </div>
                    </div>

                    <Button 
                      className="bg-[#007897] hover:bg-[#006680] mt-4" 
                      onClick={async () => {
                        try {
                          await adminAPI.updateContactInfo(contactInfo);
                          setSuccess('Contact information updated successfully');
                          setTimeout(() => setSuccess(''), 3000);
                        } catch (err) {
                          setError('Failed to update contact information');
                        }
                      }}
                    >
                      Save Contact Information
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === 'blogs' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Blog Posts</h3>
                    <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('blog')}>
                      <Plus size={18} className="mr-2" /> Add Blog Post
                    </Button>
                  </div>
                  
                  <div className="grid gap-4">
                    {blogs.length > 0 ? blogs.map((blog) => (
                      <div key={blog.id} className="bg-white rounded-xl shadow-sm p-6 flex gap-4">
                        {blog.image && (
                          <img 
                            src={blog.image} 
                            alt={blog.title} 
                            className="w-32 h-24 object-cover rounded-lg flex-shrink-0"
                            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"><rect fill="%23f3f4f6" width="200" height="150"/><text fill="%239ca3af" font-family="sans-serif" font-size="12" x="50%" y="50%" text-anchor="middle">No Image</text></svg>'; }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h4 className="font-semibold text-gray-900 truncate">{blog.title}</h4>
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{blog.excerpt}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-xs bg-[#007897]/10 text-[#007897] px-2 py-1 rounded">{blog.category}</span>
                                <span className="text-xs text-gray-400">{blog.author}</span>
                                <span className={`text-xs px-2 py-1 rounded ${blog.published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {blog.published ? 'Published' : 'Draft'}
                                </span>
                                <span className="text-xs text-gray-400">{blog.views} views</span>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <button className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => openModal('blog', blog)}>
                                <Edit size={16} className="text-blue-500" />
                              </button>
                              <button className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => handleDelete('blog', blog.id)}>
                                <Trash2 size={16} className="text-red-500" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No blog posts yet. Add your first blog post!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'testDetails' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Test Details</h3>
                    <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('testDetail')}>
                      <Plus size={18} className="mr-2" /> Add Test Detail
                    </Button>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub Service</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {testDetails.length > 0 ? testDetails.map((test) => (
                            <tr key={test.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="font-medium text-gray-900">{test.name}</div>
                                <div className="text-sm text-gray-500 truncate max-w-xs">{test.short_description}</div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {test.service_category || 'Uncategorized'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">{test.sub_service || '-'}</td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${test.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                  {test.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right space-x-2">
                                <button className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => openModal('testDetail', test)}>
                                  <Edit size={16} className="text-gray-600" />
                                </button>
                                <button className="p-2 hover:bg-red-50 rounded-lg" onClick={() => handleDelete('testDetail', test.id)}>
                                  <Trash2 size={16} className="text-red-500" />
                                </button>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="5" className="px-6 py-12 text-center">
                                <Microscope size={48} className="mx-auto text-gray-300 mb-4" />
                                <p className="text-gray-500">No test details yet. Add detailed information about your diagnostic tests!</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'heroSlides' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Hero Section</h3>
                    <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('heroSlide')}>
                      <Plus size={18} className="mr-2" /> Add Slide
                    </Button>
                  </div>
                  
                  {/* Hero Directors (Icons in Hero Section) */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Hero Section Director Icons</h4>
                    <p className="text-sm text-gray-500 mb-4">Edit the director icons displayed in the hero section banner.</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      {heroDirectors.map((director) => (
                        <div key={director.id} className="border rounded-xl p-4 flex items-start gap-4">
                          <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                            {director.image ? (
                              <img 
                                src={director.image} 
                                alt={director.name}
                                className="w-full h-full object-cover object-top"
                                onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="%23f3f4f6" width="80" height="80"/><text fill="%239ca3af" font-family="sans-serif" font-size="10" x="50%" y="50%" text-anchor="middle">Photo</text></svg>'; }}
                              />
                            ) : (
                              <span className="text-gray-400 text-xs text-center">No Photo</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900">{director.name}</h5>
                            <p className="text-sm text-[#007897]">{director.role}</p>
                            <p className="text-xs text-gray-600 mt-1">{director.qualifications}</p>
                          </div>
                          <button 
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            onClick={() => openModal('heroDirector', director)}
                          >
                            <Edit size={16} className="text-gray-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Hero Slides */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Hero Banner Slides</h4>
                    <div className="grid gap-6">
                      {heroSlides.length > 0 ? heroSlides.map((slide) => (
                        <div key={slide.id} className="border rounded-xl overflow-hidden">
                          <div className="flex">
                            <div className="w-64 h-40 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                              {slide.image ? (
                                <img 
                                  src={slide.image} 
                                  alt={slide.title} 
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="%23f3f4f6" width="400" height="300"/><text fill="%239ca3af" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle">Hero Image</text></svg>'; }}
                                />
                              ) : (
                                <span className="text-gray-400 text-sm">No Image</span>
                              )}
                            </div>
                            <div className="flex-1 p-4">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold text-gray-900 text-lg">{slide.title}</h4>
                                  <p className="text-sm text-gray-500 mt-1">{slide.subtitle}</p>
                                  <p className="text-xs text-gray-400 mt-2">{slide.description}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${slide.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                    {slide.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                    Order: {slide.order}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 mt-4">
                                <span className="text-sm text-gray-500">CTA: {slide.cta_text} → {slide.cta_link}</span>
                                <div className="flex-1" />
                                <button className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => openModal('heroSlide', slide)}>
                                  <Edit size={16} className="text-gray-600" />
                                </button>
                                <button className="p-2 hover:bg-red-50 rounded-lg" onClick={() => handleDelete('heroSlide', slide.id)}>
                                  <Trash2 size={16} className="text-red-500" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <div className="border rounded-xl p-12 text-center">
                          <Presentation size={48} className="mx-auto text-gray-300 mb-4" />
                          <p className="text-gray-500">No hero slides yet. Add slides to customize your homepage hero section!</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'aboutPage' && (
                <div className="space-y-8">
                  {/* About Page Content Section */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">About Page Content</h3>
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <ImageUpload
                            label="Hero Image"
                            value={aboutSettings.hero_image || ''}
                            onChange={(url) => setAboutSettings({...aboutSettings, hero_image: url})}
                            previewClassName="w-48 h-32 object-cover rounded-lg"
                            
                          />
                        </div>
                        <div>
                          <Label>Page Title</Label>
                          <Input 
                            value={aboutSettings.title || ''} 
                            onChange={(e) => setAboutSettings({...aboutSettings, title: e.target.value})}
                            placeholder="About Nanda Diagnostics"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Introduction Text</Label>
                        <Textarea 
                          value={aboutSettings.intro || ''} 
                          onChange={(e) => setAboutSettings({...aboutSettings, intro: e.target.value})}
                          rows={3}
                          placeholder="Welcome to Nanda Diagnostics..."
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Commitment Section Title</Label>
                          <Input 
                            value={aboutSettings.commitment_title || ''} 
                            onChange={(e) => setAboutSettings({...aboutSettings, commitment_title: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Technology Section Title</Label>
                          <Input 
                            value={aboutSettings.technology_title || ''} 
                            onChange={(e) => setAboutSettings({...aboutSettings, technology_title: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Commitment Text</Label>
                        <Textarea 
                          value={aboutSettings.commitment_text || ''} 
                          onChange={(e) => setAboutSettings({...aboutSettings, commitment_text: e.target.value})}
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>Technology Text</Label>
                        <Textarea 
                          value={aboutSettings.technology_text || ''} 
                          onChange={(e) => setAboutSettings({...aboutSettings, technology_text: e.target.value})}
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>Legacy Section Title</Label>
                        <Input 
                          value={aboutSettings.legacy_title || ''} 
                          onChange={(e) => setAboutSettings({...aboutSettings, legacy_title: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Legacy Text</Label>
                        <Textarea 
                          value={aboutSettings.legacy_text || ''} 
                          onChange={(e) => setAboutSettings({...aboutSettings, legacy_text: e.target.value})}
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>Closing Statement</Label>
                        <Textarea 
                          value={aboutSettings.closing || ''} 
                          onChange={(e) => setAboutSettings({...aboutSettings, closing: e.target.value})}
                          rows={2}
                        />
                      </div>
                      <Button 
                        className="bg-[#007897] hover:bg-[#006680]"
                        onClick={async () => {
                          try {
                            await adminAPI.updateAboutSettings(aboutSettings);
                            setSuccess('About page content updated successfully');
                            setTimeout(() => setSuccess(''), 3000);
                          } catch (err) {
                            setError('Failed to update content');
                          }
                        }}
                      >
                        Save All Content
                      </Button>
                    </div>
                  </div>

                  {/* Directors Section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Directors</h3>
                      <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('director')}>
                        <Plus size={18} className="mr-2" /> Add Director
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {directors.map((director) => (
                        <div key={director.id} className="bg-white rounded-xl shadow-sm p-4">
                          <div className="flex gap-4">
                            <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                              <img src={director.image} alt={director.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{director.name}</h4>
                              <p className="text-sm text-[#007897] font-medium">{director.role}</p>
                              <p className="text-xs text-gray-600 mt-1">{director.qualifications}</p>
                              {director.college && <p className="text-xs text-gray-500">{director.college}</p>}
                              {director.experience && (
                                <p className="text-xs text-[#007897] font-medium mt-1">{director.experience}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1">
                              <button className="p-2 hover:bg-gray-100 rounded" onClick={() => openModal('director', director)}>
                                <Edit size={14} className="text-gray-600" />
                              </button>
                              <button className="p-2 hover:bg-red-50 rounded" onClick={() => handleDelete('director', director.id)}>
                                <Trash2 size={14} className="text-red-500" />
                              </button>
                            </div>
                          </div>
                          {director.description && (
                            <p className="text-xs text-gray-500 mt-3 line-clamp-2">{director.description}</p>
                          )}
                          {director.achievements && director.achievements.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-gray-700">Achievements: {director.achievements.length} items</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Consultants Section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Consultants</h3>
                      <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('consultant')}>
                        <Plus size={18} className="mr-2" /> Add Consultant
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-2">Radiology Department</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {radiologyConsultants.map((consultant) => (
                            <div key={consultant.id} className="bg-white rounded-xl shadow-sm p-4 flex gap-4">
                              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                                {consultant.image ? (
                                  <img src={consultant.image} alt={consultant.name} className="w-full h-full object-cover" />
                                ) : (
                                  <Users size={24} className="text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 text-sm">{consultant.name}</h4>
                                <p className="text-xs text-[#007897]">{consultant.role}</p>
                                <p className="text-xs text-gray-500">{consultant.qualifications}</p>
                              </div>
                              <div className="flex flex-col gap-1">
                                <button className="p-1.5 hover:bg-gray-100 rounded" onClick={() => openModal('consultant', consultant)}>
                                  <Edit size={12} className="text-gray-600" />
                                </button>
                                <button className="p-1.5 hover:bg-red-50 rounded" onClick={() => handleDelete('consultant', consultant.id)}>
                                  <Trash2 size={12} className="text-red-500" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-2">Laboratory Department</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {laboratoryConsultants.map((consultant) => (
                            <div key={consultant.id} className="bg-white rounded-xl shadow-sm p-4 flex gap-4">
                              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                                {consultant.image ? (
                                  <img src={consultant.image} alt={consultant.name} className="w-full h-full object-cover" />
                                ) : (
                                  <Users size={24} className="text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 text-sm">{consultant.name}</h4>
                                <p className="text-xs text-[#007897]">{consultant.role}</p>
                                <p className="text-xs text-gray-500">{consultant.qualifications}</p>
                              </div>
                              <div className="flex flex-col gap-1">
                                <button className="p-1.5 hover:bg-gray-100 rounded" onClick={() => openModal('consultant', consultant)}>
                                  <Edit size={12} className="text-gray-600" />
                                </button>
                                <button className="p-1.5 hover:bg-red-50 rounded" onClick={() => handleDelete('consultant', consultant.id)}>
                                  <Trash2 size={12} className="text-red-500" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* What Makes Us Different Section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">What Makes Us Different</h3>
                      <Button className="bg-[#007897] hover:bg-[#006680]" onClick={() => openModal('differentiator')}>
                        <Plus size={18} className="mr-2" /> Add Feature
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {differentiators.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl shadow-sm p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#007897]/10 rounded-lg flex items-center justify-center">
                                <span className="text-[#007897] text-xs font-medium">{item.icon}</span>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button className="p-1.5 hover:bg-gray-100 rounded" onClick={() => openModal('differentiator', item)}>
                                <Edit size={12} className="text-gray-600" />
                              </button>
                              <button className="p-1.5 hover:bg-red-50 rounded" onClick={() => handleDelete('differentiator', item.id)}>
                                <Trash2 size={12} className="text-red-500" />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'auditLogs' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Security Audit Logs</h3>
                    <span className="text-sm text-gray-500">Last 50 activities</span>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {auditLogs.length > 0 ? auditLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-600">
                                {new Date(log.timestamp).toLocaleString()}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  log.action === 'CREATE' ? 'bg-green-100 text-green-800' :
                                  log.action === 'UPDATE' ? 'bg-blue-100 text-blue-800' :
                                  log.action === 'DELETE' ? 'bg-red-100 text-red-800' :
                                  log.action === 'LOGIN' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {log.action}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">{log.resource_type}</td>
                              <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{log.details}</td>
                              <td className="px-6 py-4 text-sm text-gray-400 font-mono">{log.ip_address || '-'}</td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="5" className="px-6 py-12 text-center">
                                <Shield size={48} className="mx-auto text-gray-300 mb-4" />
                                <p className="text-gray-500">No audit logs recorded yet. Actions will be logged here for security monitoring.</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Edit' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
              </h3>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}
              {modalType === 'package' && (
                <>
                  <div><Label>Name <span className="text-red-500">*</span></Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
                  <div><Label>Description <span className="text-gray-400 text-xs">(optional)</span></Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Brief description of the package" rows={2} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Price (₹) <span className="text-red-500">*</span></Label><Input type="number" value={formData.price || ''} onChange={(e) => setFormData({...formData, price: parseInt(e.target.value) || 0})} required min="0" /></div>
                    <div><Label>Original Price (₹) <span className="text-red-500">*</span></Label><Input type="number" value={formData.original_price || ''} onChange={(e) => setFormData({...formData, original_price: parseInt(e.target.value) || 0})} required min="0" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Category <span className="text-red-500">*</span></Label>
                      <select className="w-full border rounded-lg p-2" value={formData.category || 'Smart Wellness Packages'} onChange={(e) => setFormData({...formData, category: e.target.value})} required>
                        <option value="Advanced Wellness Packages">Advanced Wellness Packages</option>
                        <option value="Smart Wellness Packages">Smart Wellness Packages</option>
                      </select>
                    </div>
                    <div><Label>Report Time</Label><Input value={formData.report_time || 'Same Day'} onChange={(e) => setFormData({...formData, report_time: e.target.value})} placeholder="e.g., Same Day, 24 Hours" /></div>
                  </div>
                  <div>
                    <Label>Tests Included <span className="text-gray-400 text-xs">(press Enter or click + to add)</span></Label>
                    <div className="border rounded-lg p-3 mt-1 max-h-40 overflow-y-auto">
                      {(formData.tests || []).map((test, index) => (
                        <div key={`item-${index}`} className="flex items-center justify-between py-1 border-b last:border-0">
                          <span className="text-sm">{test}</span>
                          <button type="button" className="text-red-500 hover:text-red-700" onClick={() => {
                            const newTests = formData.tests.filter((_, i) => i !== index);
                            setFormData({...formData, tests: newTests});
                          }}><X size={14} /></button>
                        </div>
                      ))}
                      {(!formData.tests || formData.tests.length === 0) && <p className="text-gray-400 text-sm">No tests added yet</p>}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Input 
                        placeholder="Add new test (e.g., CBC, LFT)" 
                        id="newPackageTest"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const input = e.target;
                            if (input.value.trim()) {
                              setFormData({...formData, tests: [...(formData.tests || []), input.value.trim()]});
                              input.value = '';
                            }
                          }
                        }}
                      />
                      <Button type="button" variant="outline" onClick={() => {
                        const input = document.getElementById('newPackageTest');
                        if (input.value.trim()) {
                          setFormData({...formData, tests: [...(formData.tests || []), input.value.trim()]});
                          input.value = '';
                        }
                      }}><Plus size={16} /></Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="is_active" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} />
                      <Label htmlFor="is_active">Active (show on website)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="featured" checked={formData.featured || false} onChange={(e) => setFormData({...formData, featured: e.target.checked})} />
                      <Label htmlFor="featured">Featured (homepage)</Label>
                    </div>
                  </div>
                </>
              )}
              {modalType === 'service' && (
                <>
                  <div><Label>Name</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
                  <div><Label>Description</Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} required /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Category</Label><Input value={formData.category || ''} onChange={(e) => setFormData({...formData, category: e.target.value})} required /></div>
                    <div><Label>Icon</Label><Input value={formData.icon || ''} onChange={(e) => setFormData({...formData, icon: e.target.value})} placeholder="e.g., FlaskConical" /></div>
                  </div>
                  <div><Label>Tests (comma separated)</Label><Textarea value={formData.tests?.join(', ') || ''} onChange={(e) => setFormData({...formData, tests: e.target.value.split(',').map(t => t.trim())})} /></div>
                </>
              )}
              {modalType === 'testimonial' && (
                <>
                  <div><Label>Name</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
                  <div><Label>Source</Label><Input value={formData.source || ''} onChange={(e) => setFormData({...formData, source: e.target.value})} placeholder="e.g., Google Review" required /></div>
                  <div><Label>Review</Label><Textarea value={formData.review || ''} onChange={(e) => setFormData({...formData, review: e.target.value})} rows={4} required /></div>
                  <div><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" value={formData.rating || ''} onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})} required /></div>
                </>
              )}
              {modalType === 'inquiry' && (
                <>
                  <div><Label>Status</Label>
                    <select className="w-full border rounded-lg p-2" value={formData.status || 'pending'} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                      <option value="pending">Pending</option>
                      <option value="contacted">Contacted</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div><Label>Notes</Label><Textarea value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})} /></div>
                </>
              )}
              {modalType === 'gallery' && (
                <>
                  <div><Label>Title</Label><Input value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Reception Area" required /></div>
                  <div><Label>Category</Label>
                    <select className="w-full border rounded-lg p-2" value={formData.category || 'Interiors'} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                      <option value="Interiors">Interiors</option>
                      <option value="Exteriors">Exteriors</option>
                      <option value="Equipment">Equipment</option>
                      <option value="Team">Team</option>
                    </select>
                  </div>
                  <ImageUpload
                    label="Image"
                    value={formData.image || ''}
                    onChange={(url) => setFormData({...formData, image: url})}
                    previewClassName="w-full h-32 object-cover rounded-lg"
                    required
                    
                  />
                </>
              )}
              {modalType === 'subservice' && (
                <>
                  <div className="text-sm text-gray-500 mb-2">Adding study to: <strong>{editingItem?.serviceName}</strong></div>
                  <div><Label>Study Name</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Medical Biochemistry" required /></div>
                  <div><Label>Slug</Label><Input value={formData.slug || ''} onChange={(e) => setFormData({...formData, slug: e.target.value})} placeholder="e.g., biochemistry" /></div>
                  <div><Label>Description</Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Brief description of this study" /></div>
                </>
              )}
              {modalType === 'test' && (
                <>
                  <div className="text-sm text-gray-500 mb-2">Adding test to: <strong>{editingItem?.subServiceName}</strong></div>
                  <div><Label>Test Name</Label><Input value={formData.testName || ''} onChange={(e) => setFormData({...formData, testName: e.target.value})} placeholder="e.g., Liver Function Tests (LFT)" required /></div>
                  <div><Label>Description</Label><Textarea value={formData.testDescription || ''} onChange={(e) => setFormData({...formData, testDescription: e.target.value})} placeholder="Brief description of this test" /></div>
                </>
              )}
              {modalType === 'editTest' && (
                <>
                  <div className="text-sm text-gray-500 mb-2">Editing test in: <strong>{editingItem?.subServiceName}</strong></div>
                  <div><Label>Test Name</Label><Input value={formData.testName || ''} onChange={(e) => setFormData({...formData, testName: e.target.value})} placeholder="e.g., Liver Function Tests (LFT)" required /></div>
                  <div><Label>Description</Label><Textarea value={formData.testDescription || ''} onChange={(e) => setFormData({...formData, testDescription: e.target.value})} placeholder="Brief description of this test" rows={4} /></div>
                </>
              )}
              {modalType === 'download' && (
                <>
                  <div><Label>Title *</Label><Input value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Brochure, Report Name" required /></div>
                  <div><Label>Description</Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} placeholder="Brief description of the download" /></div>
                  <div><Label>File URL *</Label><Input value={formData.file_url || ''} onChange={(e) => setFormData({...formData, file_url: e.target.value})} placeholder="https://example.com/file.pdf" required /></div>
                  <div><Label>Category</Label>
                    <select className="w-full border rounded-lg p-2" value={formData.category || 'Brochure'} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                      <option value="Brochure">Brochure</option>
                      <option value="Report">Report</option>
                      <option value="Form">Form</option>
                      <option value="Guide">Guide</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div><Label>File Size</Label><Input value={formData.file_size || ''} onChange={(e) => setFormData({...formData, file_size: e.target.value})} placeholder="e.g., 2.5 MB" /></div>
                </>
              )}
              {modalType === 'blog' && (
                <>
                  <div><Label>Title</Label><Input value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Blog post title" required /></div>
                  <div><Label>Slug (auto-generated if empty)</Label><Input value={formData.slug || ''} onChange={(e) => setFormData({...formData, slug: e.target.value})} placeholder="e.g., health-tips-for-summer" /></div>
                  <div><Label>Excerpt/Summary</Label><Textarea value={formData.excerpt || ''} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} rows={2} placeholder="Brief summary for the blog listing" /></div>
                  <div><Label>Content</Label><Textarea value={formData.content || ''} onChange={(e) => setFormData({...formData, content: e.target.value})} rows={8} placeholder="Full blog content" required /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Author</Label><Input value={formData.author || 'Nanda Diagnostics'} onChange={(e) => setFormData({...formData, author: e.target.value})} /></div>
                    <div><Label>Category</Label>
                      <select className="w-full border rounded-lg p-2" value={formData.category || 'Health Tips'} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                        <option value="Health Tips">Health Tips</option>
                        <option value="Medical News">Medical News</option>
                        <option value="Wellness">Wellness</option>
                        <option value="Diagnostics">Diagnostics</option>
                        <option value="Prevention">Prevention</option>
                      </select>
                    </div>
                  </div>
                  <ImageUpload
                    label="Blog Image"
                    value={formData.image || ''}
                    onChange={(url) => setFormData({...formData, image: url})}
                    previewClassName="w-full h-32 object-cover rounded-lg"
                  />
                  <div><Label>Tags (comma separated)</Label><Input value={(formData.tags || []).join(', ')} onChange={(e) => setFormData({...formData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} placeholder="health, wellness, tips" /></div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="published" checked={formData.published !== false} onChange={(e) => setFormData({...formData, published: e.target.checked})} />
                    <Label htmlFor="published">Published</Label>
                  </div>
                </>
              )}
              {modalType === 'testDetail' && (
                <>
                  <div><Label>Test Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., ECHO (Echocardiography)" required /></div>
                  <div><Label>Slug (URL)</Label><Input value={formData.slug || ''} onChange={(e) => setFormData({...formData, slug: e.target.value})} placeholder="Auto-generated from name if empty" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Service Category</Label>
                      <select className="w-full border rounded-lg p-2" value={formData.service_category || ''} onChange={(e) => setFormData({...formData, service_category: e.target.value})}>
                        <option value="">Select Category</option>
                        <option value="Cardiology">Cardiology</option>
                        <option value="Laboratory">Laboratory</option>
                        <option value="Radiology">Radiology</option>
                        <option value="Electrophysiology">Electrophysiology</option>
                        <option value="Pulmonary">Pulmonary</option>
                      </select>
                    </div>
                    <div><Label>Sub Service</Label><Input value={formData.sub_service || ''} onChange={(e) => setFormData({...formData, sub_service: e.target.value})} placeholder="e.g., ECHO, Ultrasound" /></div>
                  </div>
                  <div><Label>Short Description</Label><Input value={formData.short_description || ''} onChange={(e) => setFormData({...formData, short_description: e.target.value})} placeholder="Brief one-line description" /></div>
                  <div><Label>Full Description (About the Test)</Label><Textarea rows={4} value={formData.full_description || ''} onChange={(e) => setFormData({...formData, full_description: e.target.value})} placeholder="Detailed explanation of the test..." /></div>
                  <div><Label>Why Get This Test?</Label><Textarea rows={3} value={formData.why_get_tested || ''} onChange={(e) => setFormData({...formData, why_get_tested: e.target.value})} placeholder="Explain why patients should get this test..." /></div>
                  <div><Label>Procedure (What Happens During the Test)</Label><Textarea rows={3} value={formData.procedure || ''} onChange={(e) => setFormData({...formData, procedure: e.target.value})} placeholder="Describe the procedure..." /></div>
                  <div>
                    <Label>Patient Instructions (one per line)</Label>
                    <Textarea rows={4} value={(formData.instructions || []).join('\n')} onChange={(e) => setFormData({...formData, instructions: e.target.value.split('\n').filter(i => i.trim())})} placeholder="Wear comfortable clothing&#10;Avoid eating 4 hours before&#10;Bring previous reports" />
                  </div>
                  <div>
                    <Label>Conditions Detected (one per line)</Label>
                    <Textarea rows={3} value={(formData.conditions_detected || []).join('\n')} onChange={(e) => setFormData({...formData, conditions_detected: e.target.value.split('\n').filter(c => c.trim())})} placeholder="Heart valve disorders&#10;Heart failure&#10;Cardiomyopathy" />
                  </div>
                  <ImageUpload
                    label="Test Image"
                    value={formData.image || ''}
                    onChange={(url) => setFormData({...formData, image: url})}
                    previewClassName="w-full h-32 object-cover rounded-lg"
                  />
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="is_active" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} />
                    <Label htmlFor="is_active" className="mb-0">Active (visible on website)</Label>
                  </div>
                </>
              )}
              {modalType === 'heroSlide' && (
                <>
                  <div><Label>Title *</Label><Input value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Your Trusted" required /></div>
                  <div><Label>Subtitle</Label><Input value={formData.subtitle || ''} onChange={(e) => setFormData({...formData, subtitle: e.target.value})} placeholder="e.g., Diagnostics Center" /></div>
                  <div><Label>Description</Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} placeholder="Brief description shown on the slide..." /></div>
                  <ImageUpload
                    label="Background Image"
                    value={formData.image || ''}
                    onChange={(url) => setFormData({...formData, image: url})}
                    previewClassName="w-full h-32 object-cover rounded-lg"
                    required
                    
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Background Gradient</Label>
                      <select className="w-full border rounded-lg p-2" value={formData.bg_gradient || 'from-[#007897]/70 to-[#006680]/70'} onChange={(e) => setFormData({...formData, bg_gradient: e.target.value})}>
                        <option value="from-[#006680]/70 to-[#008fa3]/70">Teal to Cyan</option>
                        <option value="from-[#007897]/70 to-[#006680]/70">Cyan to Teal</option>
                        <option value="from-[#007897]/70 to-[#008fa3]/70">Cyan to Light Cyan</option>
                        <option value="from-[#005a73]/70 to-[#007897]/70">Dark Teal to Cyan</option>
                      </select>
                    </div>
                    <div><Label>Image Position</Label>
                      <select className="w-full border rounded-lg p-2" value={formData.image_position || 'center center'} onChange={(e) => setFormData({...formData, image_position: e.target.value})}>
                        <option value="center center">Center</option>
                        <option value="center 20%">Center Top (20%)</option>
                        <option value="center 25%">Center Top (25%)</option>
                        <option value="center 30%">Center Top (30%)</option>
                        <option value="center 40%">Center (40%)</option>
                        <option value="center top">Top</option>
                        <option value="center bottom">Bottom</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>CTA Button Text</Label><Input value={formData.cta_text || 'Book Now'} onChange={(e) => setFormData({...formData, cta_text: e.target.value})} /></div>
                    <div><Label>CTA Link</Label><Input value={formData.cta_link || '/contact'} onChange={(e) => setFormData({...formData, cta_link: e.target.value})} placeholder="/contact" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Display Order</Label><Input type="number" value={formData.order || 0} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})} min="0" /></div>
                    <div className="flex items-center gap-2 pt-6">
                      <input type="checkbox" id="slide_active" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} />
                      <Label htmlFor="slide_active" className="mb-0">Active</Label>
                    </div>
                  </div>
                </>
              )}
              {modalType === 'heroDirector' && (
                <>
                  <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Dr. John Doe" required /></div>
                  <div><Label>Role *</Label><Input value={formData.role || ''} onChange={(e) => setFormData({...formData, role: e.target.value})} placeholder="Founder & Director" required /></div>
                  <div><Label>Qualifications *</Label><Input value={formData.qualifications || ''} onChange={(e) => setFormData({...formData, qualifications: e.target.value})} placeholder="MBBS, MD (Radiodiagnosis)" required /></div>
                  <ImageUpload
                    label="Photo"
                    value={formData.image || ''}
                    onChange={(url) => setFormData({...formData, image: url})}
                    previewClassName="w-20 h-20 rounded-lg object-cover object-top"
                    required
                    
                  />
                  <p className="text-xs text-gray-500">This image will appear in the hero section banner</p>
                  <div><Label>Display Order</Label><Input type="number" value={formData.order || 0} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})} min="0" /></div>
                </>
              )}
              {modalType === 'director' && (
                <>
                  <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Dr. John Doe" required /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Role *</Label><Input value={formData.role || ''} onChange={(e) => setFormData({...formData, role: e.target.value})} placeholder="Founder & Director" required /></div>
                    <div><Label>Experience</Label><Input value={formData.experience || ''} onChange={(e) => setFormData({...formData, experience: e.target.value})} placeholder="34+ years of experience in ultrasound" /></div>
                  </div>
                  <div><Label>Qualifications *</Label><Input value={formData.qualifications || ''} onChange={(e) => setFormData({...formData, qualifications: e.target.value})} placeholder="M.D. (Radiodiagnosis), M.B.B.S" required /></div>
                  <div><Label>College/Institute</Label><Input value={formData.college || ''} onChange={(e) => setFormData({...formData, college: e.target.value})} placeholder="Maulana Azad Medical College" /></div>
                  <ImageUpload
                    label="Photo"
                    value={formData.image || ''}
                    onChange={(url) => setFormData({...formData, image: url})}
                    previewClassName="w-20 h-20 rounded-lg object-cover"
                    required
                    
                  />
                  <div><Label>Description *</Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={4} placeholder="Full biography and experience details..." /></div>
                  <div>
                    <Label>Achievements/Experience Points (one per line)</Label>
                    <Textarea 
                      value={(formData.achievements || []).join('\n')} 
                      onChange={(e) => setFormData({...formData, achievements: e.target.value.split('\n').filter(a => a.trim())})} 
                      rows={4} 
                      placeholder="Established the diagnostic centre in 1992&#10;ISO 9001:2008 certified&#10;NABL accredited laboratory" 
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter each achievement on a new line</p>
                  </div>
                </>
              )}
              {modalType === 'consultant' && (
                <>
                  <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Dr. Jane Smith" required /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Role *</Label><Input value={formData.role || ''} onChange={(e) => setFormData({...formData, role: e.target.value})} placeholder="Consultant Radiologist" required /></div>
                    <div><Label>Department *</Label>
                      <select className="w-full border rounded-lg p-2" value={formData.department || 'laboratory'} onChange={(e) => setFormData({...formData, department: e.target.value})} required>
                        <option value="radiology">Radiology</option>
                        <option value="laboratory">Laboratory</option>
                      </select>
                    </div>
                  </div>
                  <div><Label>Qualifications *</Label><Input value={formData.qualifications || ''} onChange={(e) => setFormData({...formData, qualifications: e.target.value})} placeholder="MBBS, MD (Pathology)" required /></div>
                  <ImageUpload
                    label="Photo (optional - can add later)"
                    value={formData.image || ''}
                    onChange={(url) => setFormData({...formData, image: url})}
                    previewClassName="w-16 h-16 rounded-lg object-cover"
                  />
                  <div><Label>Description</Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} placeholder="Brief description..." /></div>
                </>
              )}
              {modalType === 'differentiator' && (
                <>
                  <div><Label>Icon Name *</Label>
                    <select className="w-full border rounded-lg p-2" value={formData.icon || 'Award'} onChange={(e) => setFormData({...formData, icon: e.target.value})} required>
                      <option value="Award">Award</option>
                      <option value="Building2">Building</option>
                      <option value="FlaskConical">Flask/Lab</option>
                      <option value="Home">Home</option>
                      <option value="Clock">Clock</option>
                      <option value="Heart">Heart</option>
                      <option value="Users">Users</option>
                      <option value="Shield">Shield</option>
                      <option value="Microscope">Microscope</option>
                      <option value="Stethoscope">Stethoscope</option>
                    </select>
                  </div>
                  <div><Label>Title *</Label><Input value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="NABL Accredited" required /></div>
                  <div><Label>Description *</Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} placeholder="Describe this feature..." required /></div>
                </>
              )}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={closeModal} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-[#007897] hover:bg-[#006680]">Save</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
